import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core'
import { ControlValueAccessor, FormBuilder, NG_VALUE_ACCESSOR } from '@angular/forms'
import { Facility } from '@mmx/shared'
import { eq } from 'lodash'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

@Component({
  selector: 'mmx-location-picker',
  templateUrl: './location-picker.component.html',
  styleUrls: ['./location-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: LocationPickerComponent,
      multi: true,
    },
  ],
})
export class LocationPickerComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  @Input()
  public locations: Facility[] = []

  @Input()
  public activeLocation: Facility | undefined

  @Input()
  public multiple = false

  @Output()
  public locationSelected = new EventEmitter<Facility>()

  public formGroup = this.fb.group({
    locations: this.fb.control<Facility[]>([]),
  })

  private onChange!: (value: Facility | Facility[]) => void
  private onTouched!: () => void
  private onDestroy$ = new Subject<void>()

  constructor(
    private fb: FormBuilder,
  ) { }

  public ngOnInit() {
    this.formGroup.get('locations')?.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
      )
      .subscribe((locations) => {
        if (!locations || !this.onChange) {
          return
        }

        this.onChange(this.multiple ? locations : locations[0])
      })
  }

  public ngOnChanges({ activeLocation }: SimpleChanges): void {
    if (eq(activeLocation?.previousValue, activeLocation?.currentValue)) {
      return
    }

    this.writeValue(activeLocation.currentValue || [])
  }

  public ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public isLocationSelected(location: Facility) {
    const selectedLocations = this.formGroup.get('locations')?.value || []
    if (!selectedLocations?.length) {
      return false
    }

    return !!selectedLocations.find((selectedLocation: Facility) => {
      return selectedLocation.id === location.id
    })
  }

  public onLocationPicked(location: Facility) {
    this.locationSelected.emit(location)

    const control = this.formGroup.get('locations')
    const isSelected = !!control?.value?.length && control?.value.some((selectedLocation: Facility) => (
      selectedLocation.id === location.id
    ))

    if (isSelected) {
      control?.patchValue(control.value.filter((selectedLocation: Facility) => (
        selectedLocation.id !== location.id
      )))
      return
    }

    control?.patchValue([
      ...(control.value || []),
      location,
    ])
  }

  public writeValue(location: Facility | Facility[]): void {
    this.formGroup.setValue({
      locations: Array.isArray(location) ? location : [location],
    })
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn
  }
}
